"use client"

import React, {
  forwardRef,
  useCallback,
  useMemo,
  useRef,
  useState,
  useEffect,
  type JSX,
} from "react"
import {
  AnimatePresence,
  motion,
  useMotionValue,
  useSpring,
  useTransform,
  type PanInfo,
} from "framer-motion"
import { Check, Loader2, SendHorizontal, X } from "lucide-react"

import { cn } from "@/lib/utils"
import { Button, ButtonProps } from "@/components/ui/button"

const DRAG_CONSTRAINTS = { left: 0, right: 240 }
const DRAG_THRESHOLD = 0.9

const BUTTON_STATES = {
  initial: { width: "18rem" },
  completed: { width: "10rem" },
}

const ANIMATION_CONFIG = {
  spring: {
    type: "spring",
    stiffness: 400,
    damping: 40,
    mass: 0.8,
  },
}

type StatusIconProps = {
  status: string
}

const StatusIcon: React.FC<StatusIconProps> = ({ status }) => {
  const iconMap: Record<StatusIconProps["status"], JSX.Element> = useMemo(
    () => ({
      loading: <Loader2 className="animate-spin" size={20} />,
      success: <Check size={20} />,
      error: <X size={20} />,
    }),
    []
  )

  if (!iconMap[status]) return null

  return (
    <motion.div
      key={crypto.randomUUID()}
      initial={{ opacity: 0, scale: 0.5 }}
      animate={{ opacity: 1, scale: 1 }}
      exit={{ opacity: 0 }}
    >
      {iconMap[status]}
    </motion.div>
  )
}

interface SlideButtonProps extends ButtonProps {
  onSlideComplete?: () => void;
  loading?: boolean;
  success?: boolean;
  error?: boolean;
  resetTrigger?: any; // changing this prop resets the button
}

const SlideButton = forwardRef<HTMLButtonElement, SlideButtonProps>(
  ({ className, onSlideComplete, loading, success, error, resetTrigger, ...props }, ref) => {
    const [isDragging, setIsDragging] = useState(false);
    const [completed, setCompleted] = useState(false);
    const dragHandleRef = useRef<HTMLDivElement | null>(null);
    const dragX = useMotionValue(0);
    const springX = useSpring(dragX, ANIMATION_CONFIG.spring);
    const dragProgress = useTransform(
      springX,
      [0, DRAG_CONSTRAINTS.right],
      [0, 1]
    );

    // Reset completed state if resetTrigger changes
    useEffect(() => {
      setCompleted(false);
      dragX.set(0);
    }, [resetTrigger]);

    const handleDragStart = useCallback(() => {
      if (completed || loading) return;
      setIsDragging(true);
    }, [completed, loading]);

    const handleDragEnd = () => {
      if (completed || loading) return;
      setIsDragging(false);
      const progress = dragProgress.get();
      if (progress >= DRAG_THRESHOLD) {
        setCompleted(true);
        if (onSlideComplete) onSlideComplete();
      } else {
        dragX.set(0);
      }
    };

    const handleDrag = (
      _event: MouseEvent | TouchEvent | PointerEvent,
      info: PanInfo
    ) => {
      if (completed || loading) return;
      const newX = Math.max(0, Math.min(info.offset.x, DRAG_CONSTRAINTS.right));
      dragX.set(newX);
    };

    const adjustedWidth = useTransform(springX, (x) => x + 10);

    // Determine status for icon
    let status: 'idle' | 'loading' | 'success' | 'error' = 'idle';
    if (loading) status = 'loading';
    else if (success) status = 'success';
    else if (error) status = 'error';

    return (
      <motion.div
        animate={completed ? BUTTON_STATES.completed : BUTTON_STATES.initial}
        transition={ANIMATION_CONFIG.spring}
        className="shadow-button-inset dark:shadow-button-inset-dark relative flex h-9 items-center justify-center rounded-full bg-[#AEC8A4]/35 mx-auto"
      >
        {/* Progress fill behind the handle */}
        {!completed && (
          <motion.div
            className="absolute left-0 top-0 bottom-0 rounded-full bg-[#86ad77]"
            style={{ width: springX, zIndex: 2 }}
            aria-hidden="true"
          />
        )}
        {/* Centered track text */}
        {!completed && (
          <span className="absolute left-0 right-0 mx-auto text-primary font-semibold text-base text-center pointer-events-none select-none" style={{zIndex: 1}}>
            Slide To Build Crossword
          </span>
        )}
        <AnimatePresence key={crypto.randomUUID()}>
          {!completed && (
            <motion.div
              ref={dragHandleRef}
              drag="x"
              dragConstraints={DRAG_CONSTRAINTS}
              dragElastic={0.05}
              dragMomentum={false}
              onDragStart={handleDragStart}
              onDragEnd={handleDragEnd}
              onDrag={handleDrag}
              style={{ x: springX }}
              className="absolute -left-4 z-10 flex cursor-grab items-center justify-start active:cursor-grabbing"
            >
              <div
                className={cn(
                  "rounded-full flex items-center justify-center shadow-button drop-shadow-xl transition-transform bg-primary text-white aspect-square w-10 h-10 p-0",
                  isDragging && "scale-105"
                )}
                style={{ width: 40, height: 40, borderRadius: 20 }}
              >
                <SendHorizontal className="size-5" />
              </div>
            </motion.div>
          )}
        </AnimatePresence>
        <AnimatePresence key={crypto.randomUUID()}>
          {completed && (
            <motion.div
              className="absolute inset-0 flex items-center justify-center"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
            >
              <Button
                ref={ref}
                disabled={loading}
                {...props}
                className={cn(
                  "size-full rounded-full transition-all duration-300",
                  className
                )}
              >
                <AnimatePresence key={crypto.randomUUID()} mode="wait">
                  <StatusIcon status={status} />
                </AnimatePresence>
              </Button>
            </motion.div>
          )}
        </AnimatePresence>
      </motion.div>
    );
  }
);

SlideButton.displayName = "SlideButton";

export { SlideButton }; 